import { DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Observable, share } from 'rxjs';

import { environment } from 'environments/environment';
import { Session } from '@core/service/session/session';
import { Auth } from '@features/auth/services/auth/auth';
import { ClientToServerEvents, ConnectionState, RoomId, RoomNamespace, ServerToClientEvents } from '@shared/interfaces/socket.types';

@Injectable({ providedIn: 'root' })
export class SocketService {
  private session = inject(Session);
  private destroyRef = inject(DestroyRef);

  private socket!: Socket<ServerToClientEvents, ClientToServerEvents>;
  private initialized = false;

  private rooms = new Map<RoomNamespace, Set<string>>();

  private _state = signal<ConnectionState>('disconnected');
  readonly state = this._state.asReadonly();

  constructor() {
    effect(() => {
      console.log('[Socket]', this._state());
    });
  }

  // ---------------- INIT ----------------
  init() {
    if (this.initialized) return;
    if (!this.session.getToken()) return;
    this.initialized = true;

    this.socket = io(environment.apiBase, {
      transports: ['websocket'],   // 👈 CRITICAL
      upgrade: false,              // 👈 IMPORTANT
      auth: { token: this.session.getToken() },
      extraHeaders: {
        // 'ngrok-skip-browser-warning': 'true',
      },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    this.registerLifecycle();

    // Auto join user private room
    const user = this.session.getSession()?.user;
    if (user?.id) {
      this.joinUserRoom(user.id.toString());
    }

    this.socket.on('connect_error', (error) => {
      console.error('[Socket] connect_error', error);
    });
  }

  // ---------------- ROOM API (FEATURE SAFE) ----------------
  joinUserRoom(userId: string) {
    this.join('user', RoomId.user(userId));
  }

  joinChat(chatId: string) {
    this.join('chat', RoomId.chat(chatId));
  }

  joinProject(projectId: string) {
    this.join('project', RoomId.project(projectId));
  }

  leaveChat(chatId: string) {
    this.leave('chat', RoomId.chat(chatId));
  }

  // ---------------- CORE ROOM LOGIC ----------------
  private join(type: RoomNamespace, roomId: string) {
    const set = this.rooms.get(type) ?? new Set<string>();
    if (set.has(roomId)) return;

    this.socket.emit(`${type}:join`, { roomId });
    set.add(roomId);
    this.rooms.set(type, set);
  }

  private leave(type: RoomNamespace, roomId: string) {
    const set = this.rooms.get(type);
    if (!set?.has(roomId)) return;

    this.socket.emit(`${type}:leave`, { roomId });
    set.delete(roomId);
  }

  // ---------------- EVENTS ----------------
  fromEvent<K extends keyof ServerToClientEvents>(
    event: K,
  ): Observable<ServerToClientEvents[K]> {
    return new Observable<ServerToClientEvents[K]>(observer => {

      this.init(); // 🔥 GUARANTEE socket exists

      const handler = (data: unknown) => {
        observer.next(data as ServerToClientEvents[K]);
      };

      // 🔐 Socket.IO boundary assertion (unavoidable)
      this.socket.on(event as never, handler as never);

      return () => {
        this.socket.off(event as never, handler as never);
      };
    }).pipe(share());
  }

  emit<K extends keyof ClientToServerEvents>(
    event: K,
    ...args: Parameters<ClientToServerEvents[K]>
  ) {
    this.init(); // 🔥 ENSURES CONNECTION
    this.socket.emit(event, ...args);
  }

  // ---------------- LIFECYCLE ----------------
  private registerLifecycle() {
    this.socket.on('connect', () => {
      this._state.set('connected');
      this.rejoinRooms();
    });

    this.socket.on('disconnect', () => {
      this._state.set('disconnected');
    });

    this.socket.io.on('reconnect_attempt', async () => {
      this._state.set('reconnecting');

      try {
        // const refresh = this.session.getRefreshToken();
        // if (!refresh) return;

        // // const token = await this.authService.refreshToken(refresh);
        // this.socket.auth = { token };
      } catch (e) {
        console.error('[Socket] token refresh failed', e);
      }
    });
  }

  private rejoinRooms() {
    this.rooms.forEach((set, type) =>
      set.forEach(room =>
        this.socket.emit(`${type}:join`, { roomId: room }),
      ),
    );
  }

  // ---------------- CLEANUP ----------------
  clear() {
    this.rooms.forEach((set, type) =>
      set.forEach(room => this.leave(type, room)),
    );

    this.rooms.clear();
    this.socket?.disconnect();
    this.initialized = false;
    this._state.set('disconnected');
  }


}
